/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { canadianPayrollAPI } from '../../lib/api';
import { useCanadianPayroll } from '../../context/CanadianPayrollContext';
import { toast } from 'sonner';
import { Building2, Save, Loader2 } from 'lucide-react';

/**
 * CanadianEmployerInfoSection — Settings section for CA tenants.
 *
 * Lets owners/managers set:
 *   • Employer legal name (printed at top of CA payslip PDF)
 *   • Employer address (printed under legal name)
 *
 * Hidden entirely for non-CA tenants.
 */
export function CanadianEmployerInfoSection() {
  const { caEnabled, loading: caLoading } = useCanadianPayroll();
  const [data, setData] = useState({ legal_name: '', address: '', brand_name: '' });
  const [legalName, setLegalName] = useState('');
  const [address, setAddress] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (caLoading || !caEnabled) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const r = await canadianPayrollAPI.employerInfo();
        setData(r.data);
        setLegalName(r.data.legal_name || '');
        setAddress(r.data.address || '');
      } catch {
        // 403 → not CA; gate already handles hiding
      } finally {
        setLoading(false);
      }
    })();
  }, [caEnabled, caLoading]);

  if (caLoading || !caEnabled) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await canadianPayrollAPI.updateEmployerInfo({
        legal_name: legalName.trim() || null,
        address: address.trim() || null,
      });
      setData(r.data);
      toast.success('Employer info saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-slate-200" data-testid="ca-employer-info-section">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-1">
          <Building2 className="text-slate-700" size={16} />
          <h3 className="text-sm font-semibold text-slate-800">Canadian Payroll — Employer Info</h3>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          Printed at the top of every Canadian payslip PDF. Used for record-keeping only — not for CRA filing.
        </p>

        {loading ? (
          <div className="flex items-center gap-2 text-slate-400 text-sm py-4">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-600 font-medium block mb-1">Legal Name</label>
              <Input
                value={legalName}
                onChange={(e) => setLegalName(e.target.value)}
                placeholder={data.brand_name || 'e.g. Bella Italia Inc.'}
                data-testid="ca-employer-legal-name-input"
              />
            </div>
            <div>
              <label className="text-xs text-slate-600 font-medium block mb-1">Address</label>
              <Input
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="123 Saint-Catherine St, Montreal, QC H3B 1A1"
                data-testid="ca-employer-address-input"
              />
            </div>
            <div className="col-span-full">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="ca-employer-save-btn"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Save Employer Info
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default CanadianEmployerInfoSection;
